import { ComponentCO2 } from '@acies/domain';
import { ObservableCollection } from '@acies/observables';
import { CheckBoxEvent } from '@acies/ui';
import NetworkError from '@shared/errors/network-error';
import CO2Model from '../models/co2-model';
import DeleteComponentCO2Model from '../models/delete-component-co2-model';
import ListComponentCO2Model from '../models/list-component-co2-model';
import CO2Service from '../services/co2-service';

export default class DeleteComponentCO2Controller {
	public dialog: HTMLDialogElement | null;
	#deleteComponentCO2Model: DeleteComponentCO2Model;
	#listComponentCO2Model: ListComponentCO2Model;
	#co2Service: CO2Service;
	#co2Collection: ObservableCollection<ComponentCO2>;
	public constructor(co2Model: CO2Model, co2Service: CO2Service) {
		this.dialog = null;
		this.#deleteComponentCO2Model = co2Model.deleteComponentCO2Model;
		this.#listComponentCO2Model = co2Model.listComponentCO2Model;
		this.#co2Service = co2Service;
		this.#co2Collection = this.#listComponentCO2Model.co2Collection;
		this.co2CollectionChanged = this.co2CollectionChanged.bind(this);
		this.#co2Collection.subscribe(this.co2CollectionChanged);
		this.deleteNextComponentCO2 = this.deleteNextComponentCO2.bind(this);
	}

	private co2CollectionChanged(): void {
		this.#deleteComponentCO2Model.itemsToBeDeleted.clear();
		this.#updateDeleteButtonEnabled();
		this.#deleteComponentCO2Model.selectAllCheckBoxSelected = false;
		this.#updateSelectAllCheckBoxEnabled();
	}

	public selectAllComponentCO2CheckBoxChanged(event: Event): void {
		if (event instanceof CheckBoxEvent) {
			if (event.selected) {
				this.#selectAllCheckBoxSelected();
			} else {
				this.#selectAllCheckBoxUnSelected();
			}
			// syncronize the model with the event selected property
			this.#deleteComponentCO2Model.selectAllCheckBoxSelected = event.selected;
		}
	}

	public closeDialog(): void {
		if (this.dialog) {
			this.dialog.close();
			this.#hideAllErrorLabels();
		}
	}

	public dialogDeleteButtonClicked(): void {
		this.#deleteComponentCO2Model.dialogCancelButtonEnabled = false;
		this.#deleteComponentCO2Model.dialogDeleteButtonEnabled = false;
		this.#deleteComponentCO2Model.deleteSpinnerVisible = true;
		this.#co2Collection.unsubscribe(this.co2CollectionChanged);
		this.#hideAllErrorLabels();
		this.deleteNextComponentCO2();
	}

	public deleteCheckBoxToogle(event: Event): void {
		if (event instanceof CheckBoxEvent && event.item instanceof ComponentCO2) {
			if (event.selected) {
				this.#deleteComponentCO2Model.itemsToBeDeleted.addItem(event.item);
			} else {
				this.#deleteComponentCO2Model.itemsToBeDeleted.removeItem(event.item);
			}
			this.#updateDeleteButtonEnabled();
			// any of the items are toggled, we unselect the select all checkbox
			this.#deleteComponentCO2Model.selectAllCheckBoxSelected = false;
		}
	}

	public showDialog(): void {
		if (this.dialog) {
			this.dialog.showModal();
		}
	}

	private deleteNextComponentCO2(): void {
		this.#hideAllErrorLabels();
		const componentCO2 = this.#deleteComponentCO2Model.itemsToBeDeleted.getItemAt(0);
		// if componentCO2 is not null, we can safely remove
		// it from the Collection and delete it on the server.
		if (componentCO2) {
			this.#co2Service.deleteComponentCO2(componentCO2).subscribe((response) => {
				if (response instanceof ComponentCO2) {
					this.#deleteComponentCO2Model.itemsToBeDeleted.removeItem(response);
					this.#co2Collection.removeItem(response);
					this.deleteNextComponentCO2();
				} else {
					// if we encounter an error during batch deletion, give the user,
					// the chance to continue with deletion, if for example, the error
					// was a temporary NetworkError.
					this.#co2Collection.subscribe(this.co2CollectionChanged);
					this.#deleteComponentCO2Model.dialogCancelButtonEnabled = true;
					this.#deleteComponentCO2Model.dialogDeleteButtonEnabled = true;
					this.#deleteComponentCO2Model.deleteSpinnerVisible = false;
					this.#showErrorLabel(response);
				}
			});
		} else {
			// if componentCO2 is null, we are done deleting.
			this.#co2Collection.subscribe(this.co2CollectionChanged);
			this.#deleteComponentCO2Model.dialogCancelButtonEnabled = true;
			this.#deleteComponentCO2Model.dialogDeleteButtonEnabled = true;
			this.#deleteComponentCO2Model.deleteSpinnerVisible = false;
			this.#updateDeleteButtonEnabled();
			this.#hideAllErrorLabels();
			this.closeDialog();
			this.#updateSelectAllCheckBoxEnabled();
		}
	}

	#showErrorLabel(error: NetworkError): void {
		if (error instanceof NetworkError) {
			this.#deleteComponentCO2Model.networkErrorLabelVisible = true;
		} else {
			this.#deleteComponentCO2Model.unknownErrorLabelVisible = true;
		}
	}

	#hideAllErrorLabels(): void {
		this.#deleteComponentCO2Model.networkErrorLabelVisible = false;
		this.#deleteComponentCO2Model.unknownErrorLabelVisible = false;
	}

	#updateDeleteButtonEnabled(): void {
		this.#deleteComponentCO2Model.deleteButtonEnabled = this.#deleteComponentCO2Model.itemsToBeDeleted.length > 0;
	}

	#updateSelectAllCheckBoxEnabled(): void {
		// the co2Collection.collection contains the filtered items, so use that to update enabled true / false
		this.#listComponentCO2Model.selectAllCheckBoxEnabled = this.#co2Collection.collection.length > 0;
	}

	#selectAllCheckBoxSelected(): void {
		this.#deleteComponentCO2Model.itemsToBeDeleted.clear();
		for (const item of this.#co2Collection.collection) {
			item.selected.value = false;
			item.selected.value = true;
			this.#deleteComponentCO2Model.itemsToBeDeleted.addItem(item);
		}
		this.#updateDeleteButtonEnabled();
	}

	#selectAllCheckBoxUnSelected(): void {
		this.#deleteComponentCO2Model.itemsToBeDeleted.clear();
		for (const item of this.#listComponentCO2Model.co2Collection.collection) {
			item.selected.value = true;
			item.selected.value = false;
		}
		this.#updateDeleteButtonEnabled();
	}
}
